<template>
  <div class="insurance-cases-list">

    <div v-if="isLoading && !hasFilteredCases" class="list-loading" v-loading="true" />

    <div v-else-if="!hasFilteredCases" class="empty-state-container">
      <EmptyState message="Nenalezeny žádné pojistné události" />
    </div>

    <!-- Table variant for md and up -->
    <div v-else-if="isMdUp" class="cases-collapse" v-loading="isLoading">
      <el-row>
        <el-col :span="24">
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
          <el-table :data="displayedCases" :border="false" :preserve-expanded-content="false" table-layout="auto"
            style="width: 100%;" :stripe="true" class="cases-table">
            <!-- @vue-generic {InsuranceCase} -->
            <el-table-column type="expand">
              <template #default="props">
                <el-card class="bg-grey">
                  <el-row :gutter="20">
                    <el-col :md="12" :lg="12" :xl="12">
                      <ValueItem v-if="props.row.reportNumber" label="Hlášenka:" :value="props.row.reportNumber" />
                      <ValueItem label="Datum vzniku:" :value="formatDate(props.row.date, 'neznámý')" />
                      <ValueItem v-if="props.row.endDate" label="Datum ukončení:"
                        :value="formatDate(props.row.endDate, 'neznámý')" />
                      <ValueItem v-if="false" label="Pojišťovna:" :value="props.row.partner ?? undefined" />
                    </el-col>
                    <el-col :md="12" :lg="12" :xl="12">
                      <div>
                        <ValueItem v-if="props.row.damageType" label="Typ škody:" :value="props.row.damageType" />
                        <ValueItem v-if="props.row.representationType" label="Typ zastoupení:"
                          :value="props.row.representationType" />
                        <ValueItem v-if="props.row.RZ" label="RZ:" :value="props.row.RZ" />
                        <ValueItem v-if="props.row.location" label="Místo:" :value="props.row.location" />
                      </div>
                    </el-col>
                  </el-row>
                </el-card>
              </template>
            </el-table-column>
            <!-- @vue-generic {InsuranceCase} -->
            <el-table-column label="Událost" prop="caseNumber">
              <template #default="props">
                <RouterLinkSmart :to="getInsuranceCaseRoute(props.row)!" class="case-main-info"
                  @click="handleInsuranceCaseClick($event, props.row)">
                  <div class="case-status">
                    <InsuranceCaseStatusTag :status="props.row.status" size="small" />
                  </div>
                  <div class="case-number">
                    <span class="case-number-text">{{ props.row.caseNumber }}</span>
                  </div>
                </RouterLinkSmart>
              </template>
            </el-table-column>
            <!-- @vue-generic {InsuranceCase} -->
            <el-table-column v-for="columnKey in props.tableColumns" :key="columnKey"
              :label="columnConfig[columnKey].label">
              <template #default="props">
                {{ columnConfig[columnKey].formatter(props.row) }}
              </template>
            </el-table-column>

          </el-table>

          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
        </el-col>
      </el-row>
    </div>

    <!-- Card variant for below md -->
    <div v-else class="cases-cards" v-loading="isLoading">
      <el-row :gutter="20" class="h-full">
        <el-col :sm="24">
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
        </el-col>
        <el-col v-for="insuranceCase in displayedCases" :key="insuranceCase.caseNumber" :xs="24" :sm="24" :md="12"
          :lg="12" :xl="8" class="case-card-col">
          <RouterLinkSmart :to="getInsuranceCaseRoute(insuranceCase)!" class="case-card-link"
            @click="handleInsuranceCaseClick($event, insuranceCase)">
            <BaseCard :icon="Document" :title="insuranceCase.caseNumber" class="case-card">


              <template #header-right>
                <InsuranceCaseStatusTag :status="insuranceCase.status" />
              </template>
              <div class="overview-list">
                <ValueItem label="Hlášenka:" v-if="insuranceCase.reportNumber" :value="insuranceCase.reportNumber" />

                <ValueItem label="Datum vzniku:"
                  :value="insuranceCase.date ? formatDate(insuranceCase.date) : 'neznámý'" />

                <ValueItem v-if="insuranceCase.endDate" label="Datum ukončení:"
                  :value="insuranceCase.endDate ? formatDate(insuranceCase.endDate) : 'N/A'" />

                <ValueItem v-if="insuranceCase.partner" label="Pojišťovna:" :value="insuranceCase.partner" />
              </div>
            </BaseCard>
          </RouterLinkSmart>
        </el-col>
        <el-col :sm="24">
          <ListPager v-model:currentPage="currentPage" :pageSize="itemsPerPage" :total="totalCount" />
        </el-col>
      </el-row>
    </div>
  </div>

</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { ElRow, ElCol, ElTable, ElTableColumn, ElCard } from 'element-plus'
import { Document } from '@element-plus/icons-vue'
import type { InsuranceCase } from '@/types/customer'
import BaseCard from '@/components/common/BaseCard.vue'
import RouterLinkSmart from '@/components/common/RouterLinkSmart.vue'
import ValueItem from '@/components/common/ValueItem.vue'
import ListPager from '@/components/common/ListPager.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import InsuranceCaseStatusTag from '@/components/insurance/InsuranceCaseStatusTag.vue'
import { formatDate } from '@/utils/formatters'
import { useResourceNavigation } from '@/composables/useResourceNavigation'

// Props interface
interface Props {
  totalCount: number
  filteredInsuranceCases: InsuranceCase[] | null
  isLoading: boolean
  tableColumns?: ('reportNumber' | 'date' | 'endDate' | 'partner' | 'damageType' | 'representationType' | 'RZ' | 'location')[]
  disableNavigation?: boolean
}

// Props
const props = withDefaults(defineProps<Props>(), {
  tableColumns: () => ['reportNumber', 'date', 'partner'],
  disableNavigation: false,
})

// Emits
const emit = defineEmits<{
  select: [insuranceCase: InsuranceCase]
}>()

// Navigation
const { getInsuranceCaseRoute } = useResourceNavigation()

// Click handler for insurance case selection
function handleInsuranceCaseClick(event: MouseEvent, insuranceCase: InsuranceCase) {
  emit('select', insuranceCase)
  if (props.disableNavigation) {
    event.preventDefault()
  }
}


// Column configuration
const columnConfig = {
  reportNumber: {
    label: 'Hlášenka',
    formatter: (row: InsuranceCase) => row.reportNumber || ''
  },
  date: {
    label: 'Datum vzniku',
    formatter: (row: InsuranceCase) => row.date ? formatDate(row.date) : 'neznámý'
  },
  endDate: {
    label: 'Datum ukončení',
    formatter: (row: InsuranceCase) => row.endDate ? formatDate(row.endDate) : 'N/A'
  },
  partner: {
    label: 'Pojišťovna',
    formatter: (row: InsuranceCase) => row.partner || ''
  },
  damageType: {
    label: 'Typ škody',
    formatter: (row: InsuranceCase) => row.damageType || ''
  },
  representationType: {
    label: 'Typ zastoupení',
    formatter: (row: InsuranceCase) => row.representationType || ''
  },
  RZ: {
    label: 'RZ',
    formatter: (row: InsuranceCase) => row.RZ || '--'
  },
  location: {
    label: 'Místo',
    formatter: (row: InsuranceCase) => row.location || '--'
  }
}

// Breakpoint state (md and up => table)
const isMdUp = ref(false)
let mediaQuery: MediaQueryList | null = null
onMounted(() => {
  mediaQuery = window.matchMedia('(min-width: 768px)')
  const update = () => { isMdUp.value = !!mediaQuery && mediaQuery.matches }
  update()
  mediaQuery.addEventListener('change', update)
})
onBeforeUnmount(() => {
  // remove listener if created
  try {
    mediaQuery?.removeEventListener('change', () => { })
  } catch { }
})

// Computed properties
const hasFilteredCases = computed(() => (props.filteredInsuranceCases?.length || 0) > 0)

// Pagination state shared for both variants
const currentPage = ref(1)
const itemsPerPage = computed(() => (isMdUp.value ? 10 : 5))
const totalCount = computed(() => props.filteredInsuranceCases?.length || 0)

// Displayed cases (limited subset for performance)
const displayedCases = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value
  const endIndex = currentPage.value * itemsPerPage.value
  return props.filteredInsuranceCases?.slice(startIndex, endIndex) || []
})

// Reset pagination when filtered cases or layout change
watch(() => props.filteredInsuranceCases, () => { currentPage.value = 1 }, { deep: true })
watch(isMdUp, () => { currentPage.value = 1 })
</script>

<style scoped>
.insurance-cases-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

}

.list-loading {
  flex: 1;
  min-height: 280px;
  border-radius: 8px;
}

.bg-grey {
  background-color: var(--el-color-info-light-9);
}

/* Cards variant */
.case-card-col {
  margin-bottom: 10px;
}

.case-card-link {
  display: block;
}


.cases-cards {
  margin-top: 20px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: 100%;
}

.case-card {
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  text-decoration: none;
    color: inherit;
    display: block;
}

.case-card:hover {
  transform: translateY(-2px);
}

/* Table variant */
.cases-collapse {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: 100%;
}

.cases-table {
  --el-table-header-bg-color: var(--el-color-info-light-8);
}

.case-main-info {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}


.case-number-text {
  font-weight: 600;
  font-size: 16px;
  color: var(--el-text-color-primary);
}

.case-status {
  flex-shrink: 0;
}

/* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .insurance-cases-list {
      padding: 12px;
    }

    .case-card {
      margin-bottom: 16px;
    }
  }

  @media (max-width: 480px) {
    .insurance-cases-list {
      padding: 8px;
    }

    .case-card {
      margin-bottom: 12px;
  }
}
</style>
